import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "./ui";

/**
 * Keeps one broken page from taking the whole app with it.
 *
 * React unmounts the entire tree when a render throws, so without this a single
 * bad component leaves a white screen with no navigation on it -- nothing to
 * click, and the person has to know to edit the URL. That is what a null list
 * from the API did to the Developers page.
 *
 * It sits inside the shell, around the routed page only, so the sidebar and the
 * header survive and the next click goes somewhere real. Remounting on a route
 * change is what clears it: `resetKey` is the pathname.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; resetKey?: string },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The console is where this is actually read, from a customer's own
    // browser on a call. There is no error reporting service wired up yet.
    console.error("Page crashed:", error, info.componentStack);
  }

  componentDidUpdate(previous: { resetKey?: string }) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="ws-card p-8 text-center">
        <h1 className="text-lg font-semibold tracking-tight">This page did not load</h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-ink-500">
          Something went wrong on our side. Nothing you were working on has been lost. Try again, or
          use the menu to go somewhere else.
        </p>
        <div className="mt-5 flex justify-center gap-3">
          <Button onClick={() => this.setState({ failed: false })}>Try again</Button>
          <Button variant="secondary" onClick={() => window.location.assign("/app")}>
            Go to the dashboard
          </Button>
        </div>
      </div>
    );
  }
}
